/**
 * Field-aware formatting for Immo 360 import preview change summaries.
 * Converts known CAD-cent integers for display only; does not mutate inputs.
 */

import {
  formatAdminProspectCents,
  adminRegistryActivityTypeLabel,
} from './adminProspectDisplay.js';

/** Nested / leaf keys that represent integer CAD cents. */
export const IMPORT_PREVIEW_CENTS_KEYS = Object.freeze([
  'totalCents',
  'landCents',
  'buildingCents',
  'considerationCents',
  'municipalAssessmentTotalCents',
]);

/** Nested keys that are calendar years or plain counts — never divide by 100. */
export const IMPORT_PREVIEW_NON_MONEY_NUMBER_KEYS = Object.freeze([
  'constructionYear',
  'assessmentYear',
  'referenceYear',
  'numberOfUnits',
  'numberOfFloors',
  'unitCount',
  'floorCount',
  'count',
  'transferredPercentage',
]);

const NESTED_LABEL_KEYS = Object.freeze({
  totalCents: 'admin.prospection.import.nested.totalCents',
  landCents: 'admin.prospection.import.nested.landCents',
  buildingCents: 'admin.prospection.import.nested.buildingCents',
  considerationCents: 'admin.prospection.import.nested.considerationCents',
  municipalAssessmentTotalCents: 'admin.prospection.import.nested.totalCents',
  buildingCategory: 'admin.prospection.import.nested.buildingCategory',
  constructionType: 'admin.prospection.import.nested.constructionType',
  numberOfUnits: 'admin.prospection.import.nested.numberOfUnits',
  numberOfFloors: 'admin.prospection.import.nested.numberOfFloors',
  constructionYear: 'admin.prospection.import.nested.constructionYear',
  activityType: 'admin.prospection.import.nested.activityType',
  activityDate: 'admin.prospection.import.nested.activityDate',
  originalLabel: 'admin.prospection.import.nested.originalLabel',
  assessmentYear: 'admin.prospection.import.nested.assessmentYear',
  referenceYear: 'admin.prospection.import.nested.assessmentYear',
  referenceDate: 'admin.prospection.import.nested.activityDate',
  registrationNumber: 'admin.prospection.import.nested.registrationNumber',
  transferredPercentage: 'admin.prospection.import.nested.transferredPercentage',
  landArea: 'admin.prospection.import.nested.landArea',
});

function translatedOrFallback(key, t, fallback) {
  const label = t(key);
  return label === key ? fallback : label;
}

export function importPreviewNestedFieldLabel(key, t) {
  const i18nKey = NESTED_LABEL_KEYS[key];
  if (!i18nKey) return null;
  return translatedOrFallback(i18nKey, t, key);
}

export function importPreviewTopFieldLabel(field, t) {
  const key = `admin.prospection.import.field.${field}`;
  const label = t(key);
  return label === key ? field : label;
}

/**
 * Format integer CAD cents for display. Zero is a valid amount.
 * @returns {string|null}
 */
export function formatImportPreviewCadCents(cents, language) {
  if (cents === null || cents === undefined) return null;
  if (typeof cents === 'boolean') return null;
  if (!Number.isInteger(cents) || cents < 0) return null;
  const formatted = formatAdminProspectCents(cents, language === 'en' ? 'en' : 'fr');
  return formatted.kind === 'currency' ? formatted.text : null;
}

/**
 * Format a YYYY-MM-DD (or YYYY/MM/DD) value without timezone shift.
 */
export function formatImportPreviewDateOnly(raw, language) {
  if (raw == null) return null;
  if (typeof raw !== 'string') return null;
  const text = raw.trim();
  const match = text.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
  if (!match) return text;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return text;
  const locale = language === 'en' ? 'en-CA' : 'fr-CA';
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return text;
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

function formatLeafValue(key, value, t, language) {
  if (value == null) return t('admin.prospection.import.value.unavailable');
  if (value === '[set]') return t('admin.prospection.import.value.set');
  if (value === '[updated]') return t('admin.prospection.import.value.updated');

  if (IMPORT_PREVIEW_CENTS_KEYS.includes(key) && Number.isInteger(value)) {
    const money = formatImportPreviewCadCents(value, language);
    if (money != null) return money;
  }

  if (key === 'activityType' && typeof value === 'string') {
    const typeLabel = adminRegistryActivityTypeLabel(value, t);
    if (typeLabel) return typeLabel;
    return value;
  }

  if ((key === 'activityDate' || key === 'referenceDate') && typeof value === 'string') {
    return formatImportPreviewDateOnly(value, language) || value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((item) => String(item)).join(', ');
  return null;
}

function formatObjectValue(value, t, language) {
  if (Number.isInteger(value.count) && Object.keys(value).every((k) => k === 'count')) {
    return t('admin.prospection.import.value.ownerCount').replace('{count}', String(value.count));
  }

  const entries = Object.entries(value).filter(([, nested]) => nested != null);
  if (
    entries.length === 1 &&
    IMPORT_PREVIEW_CENTS_KEYS.includes(entries[0][0]) &&
    Number.isInteger(entries[0][1])
  ) {
    const money = formatImportPreviewCadCents(entries[0][1], language);
    if (money != null) return money;
  }

  const parts = [];
  for (const [key, nested] of entries) {
    if (key === 'provenance' || key === 'addressKey' || key === 'lotMatchKeys') continue;
    const leaf = formatLeafValue(key, nested, t, language);
    if (leaf == null) {
      if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        const nestedText = formatObjectValue(nested, t, language);
        const label = importPreviewNestedFieldLabel(key, t);
        parts.push(label ? `${label}: ${nestedText}` : nestedText);
      }
      continue;
    }
    const label = importPreviewNestedFieldLabel(key, t);
    parts.push(label ? `${label}: ${leaf}` : leaf);
  }
  if (parts.length) return parts.join(' · ');
  return t('admin.prospection.import.value.updated');
}

/**
 * Format a change current/incoming value for preview display.
 */
export function formatImportPreviewValue(value, t, language = 'fr', field = null) {
  if (value == null) return t('admin.prospection.import.value.unavailable');
  if (value === '[set]') return t('admin.prospection.import.value.set');
  if (value === '[updated]') return t('admin.prospection.import.value.updated');

  if (typeof value === 'number') {
    if (field && IMPORT_PREVIEW_CENTS_KEYS.includes(field) && Number.isInteger(value)) {
      const money = formatImportPreviewCadCents(value, language);
      if (money != null) return money;
    }
    return String(value);
  }
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'string') {
    if (field === 'activityDate' || field === 'activityType') {
      const leaf = formatLeafValue(field, value, t, language);
      if (leaf != null) return leaf;
    }
    return value;
  }
  if (Array.isArray(value)) return value.map((item) => String(item)).join(', ');
  if (typeof value === 'object') return formatObjectValue(value, t, language);
  return String(value);
}

/**
 * Build the multi-line change summary shown under will_update rows.
 * Does not mutate `row` or nested change objects.
 */
export function formatImportPreviewChangeSummary(row, t, language = 'fr') {
  const summary = row?.changeSummary;
  if (!summary) return '';
  if (Array.isArray(summary.changes) && summary.changes.length) {
    return summary.changes
      .map((change) =>
        t('admin.prospection.import.changeLine')
          .replace('{field}', importPreviewTopFieldLabel(change.field, t))
          .replace('{current}', formatImportPreviewValue(change.current, t, language, change.field))
          .replace('{incoming}', formatImportPreviewValue(change.incoming, t, language, change.field)),
      )
      .join('\n');
  }
  if (!Array.isArray(summary.changedFields) || !summary.changedFields.length) return '';
  const fields = summary.changedFields.map((field) => importPreviewTopFieldLabel(field, t)).join(', ');
  return t('admin.prospection.import.changeSummary')
    .replace('{count}', String(summary.changeCount || summary.changedFields.length))
    .replace('{fields}', fields);
}

/** Snapshot helper for tests asserting non-mutation. */
export function snapshotPreviewValue(value) {
  return JSON.stringify(value);
}
